import { getErrorMessage } from "@/helpers/handleErrorResponse";
import {
  GetAnalysisWidgetConfigResponse,
  PostAnalysisWidgetAnalyticsPayload,
  PostAnalysisWidgetDefaultConfigPayload,
  PostWidgetAnalysisConfigPayload,
  PostWidgetAnalysisResponse,
  PostWidgetDefaultConfigPayload,
  PostWidgetDefaultConfigResponse,
  PutAnalysisWidgetConfigPayload
} from "@/types/widget";
import { CustomError } from "@/utils/errors";

import axiosInstance from "../config";

export const postWidgetAnalysisConfig = async (
  body: PostWidgetAnalysisConfigPayload
): Promise<PostWidgetAnalysisResponse> => {
  const { id, ...payload } = body;

  try {
    const { data } = await axiosInstance.post(
      `/v1/datasets/${id}/analytics`,
      payload
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postWidgetDefaultConfig = async (
  body: PostWidgetDefaultConfigPayload
) => {
  const { id, grid_config, group_by } = body;

  try {
    const { data } = await axiosInstance.post(
      `/v1/datasets/${id}/default-widgets`,
      // only include grid_config and group_by if they are provided.
      // it for H3 Dataset which requires grid_config to determine the default widget
      {
        ...(grid_config?.aggregation_type && { grid_config }),
        ...(group_by && group_by !== "none" && { group_by })
      }
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    const newError = new CustomError(message, status);
    throw newError;
  }
};

export const postAnalysisWidgetAnalyticsConfig = async (
  body: PostAnalysisWidgetAnalyticsPayload
): Promise<PostWidgetAnalysisResponse> => {
  const { analysisId, datasetId, ...payload } = body;

  try {
    const { data } = await axiosInstance.post(
      `/v1/analysis/${analysisId}/datasets/${datasetId}/analytics`,
      payload
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    throw new CustomError(message, status);
  }
};

export const postAnalysisWidgetDefaultConfig = async (
  body: PostAnalysisWidgetDefaultConfigPayload
): Promise<PostWidgetDefaultConfigResponse> => {
  const { analysisId, datasetId, grid_config, group_by } = body;

  try {
    const { data } = await axiosInstance.post(
      `/v1/analysis/${analysisId}/datasets/${datasetId}/default-widgets`,
      {
        ...(grid_config?.aggregation_type && { grid_config }),
        ...(group_by && group_by !== "none" && { group_by })
      }
    );
    return data;
  } catch (error) {
    const { status, message } = getErrorMessage(error);
    throw new CustomError(message, status);
  }
};

export const putAnalysisWidgetConfig = async (
  analysisId: string,
  payload: PutAnalysisWidgetConfigPayload
) => {
  try {
    const { data } = await axiosInstance.put(
      `/v1/analysis/${analysisId}/widget-config`,
      payload
    );
    return data;
  } catch (error) {
    const { status, message, details } = getErrorMessage(error);
    throw new CustomError(message, status, details);
  }
};

export const getAnalysisWidgetConfig = async (
  analysisId: string
): Promise<GetAnalysisWidgetConfigResponse> => {
  try {
    const { data } = await axiosInstance.get(
      `/v1/analysis/${analysisId}/widget-config`
    );
    return data;
  } catch (error) {
    const { status, message, details } = getErrorMessage(error);
    throw new CustomError(message, status, details);
  }
};
